import {
    ArrowLeftOutlined,
    GlobalOutlined,
    PauseOutlined,
    PlayCircleOutlined,
    SettingOutlined,
} from '@ant-design/icons'
import { Link } from '@tanstack/react-router'
import { Alert, Button, Flex, Switch, Tag, Tooltip } from 'antd'
import React, { ReactNode } from 'react'
import { Campaign } from '../campaign'
import { Container } from '../../components/layout/container'
import { PageHeader, PageHeaderProps } from '../../components/page-header'
import { useUpdateCampaign } from '../hooks'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { UPDATE_CAMPAIGN } from '../../user/permission'
import { CampaignImportModalProps, CandidateImportModal } from '../../candidate/components/candidate-import-modal'
import { NotSubscribedResult } from '../../components/not-subscribed-result'
import { onErrorNotifyAboutValidationError } from '../../util'

export type CampaignViewProps = {
    campaign: Campaign
    children: ReactNode
    candidateImportModal: Omit<CampaignImportModalProps, 'campaignId'>
    pageHeader?: Omit<PageHeaderProps, 'title'>
    headerActions?: ReactNode
    backToBoard?: boolean
    // Render children even without an active subscription (e.g. campaign settings stay accessible).
    allowWithoutSubscription?: boolean
}

export function ViewCampaign({
    campaign,
    children,
    candidateImportModal,
    pageHeader,
    headerActions,
    backToBoard = false,
    allowWithoutSubscription = false,
}: CampaignViewProps) {
    const { t } = useTranslation()
    const updateCampaign = useUpdateCampaign(campaign.id)
    const { can, hasSubscription } = useAuth()

    return (
        <>
            <PageHeader
                title={campaign.title}
                {...pageHeader}
                suffix={
                    <>
                        {campaign.website && <WebsiteLabel website={campaign.website} />}
                        {hasSubscription && pageHeader?.suffix}
                    </>
                }
            >
                {can(UPDATE_CAMPAIGN) && (
                    <Flex
                        align="center"
                        gap="small"
                    >
                        {headerActions}
                        {hasSubscription && (
                            <Tooltip
                                title={
                                    campaign.status === 'active' ? t('Pause conversations') : t('Resume conversations')
                                }
                            >
                                <Switch
                                    onChange={() => {
                                        updateCampaign
                                            .mutateAsync({
                                                status: campaign.status === 'active' ? 'inactive' : 'active',
                                            })
                                            .catch(onErrorNotifyAboutValidationError)
                                    }}
                                    loading={updateCampaign.isPending}
                                    disabled={!campaign.canStart}
                                    checked={campaign.status === 'active'}
                                    checkedChildren={<PauseOutlined />}
                                    unCheckedChildren={<PlayCircleOutlined />}
                                />
                            </Tooltip>
                        )}
                        <Tooltip title={t(backToBoard ? 'Back to candidate board' : 'Settings')}>
                            <Link
                                to={backToBoard ? '/campaigns/$campaignId/board' : '/campaigns/$campaignId/settings'}
                                className="mt-0.5"
                                aria-label={t(backToBoard ? 'Back to candidate board' : 'Settings')}
                                params={{
                                    campaignId: campaign.id,
                                }}
                            >
                                <Button
                                    type="text"
                                    size="small"
                                >
                                    {backToBoard ? (
                                        <ArrowLeftOutlined className="text-lg" />
                                    ) : (
                                        <SettingOutlined className="text-lg" />
                                    )}
                                </Button>
                            </Link>
                        </Tooltip>
                    </Flex>
                )}
            </PageHeader>
            <Container className="py-6">
                {hasSubscription || allowWithoutSubscription ? (
                    <>
                        {campaign.startMessageTemplate && campaign.startMessageTemplate?.status !== 'APPROVED' && (
                            <Alert
                                className="mb-6"
                                type="warning"
                                message={t('Campaign blocked because start message WhatsApp template is not approved')}
                            />
                        )}
                        {children}
                    </>
                ) : (
                    <NotSubscribedResult withCreateCampaign={false} />
                )}
            </Container>
            <CandidateImportModal
                {...candidateImportModal}
                campaignId={campaign.id}
            />
        </>
    )
}

function WebsiteLabel({ website }: { website: { siteId: string; siteName: string; vacancyUrl: string } }) {
    const { t } = useTranslation()

    return (
        <Tag
            icon={<GlobalOutlined />}
            className="ml-2"
        >
            <Link
                to="/websites/$siteId"
                params={{ siteId: website.siteId }}
            >
                {t('From website: {{name}}', { name: website.siteName })}
            </Link>
            {' · '}
            <a
                href={website.vacancyUrl}
                target="_blank"
                rel="noreferrer"
            >
                {t('vacancy page')} ↗
            </a>
        </Tag>
    )
}
